export type PlanFeature =
  | 'orders'
  | 'payments'
  | 'products'
  | 'inventory'
  | 'customers'
  | 'website'
  | 'journal'
  | 'staff'
  | 'reports'
  | 'quotations'
  | 'purchase_orders'
  | 'rentals'
  | 'scheduling'
  | 'payroll'
  | 'marketing'
  | 'messaging'
  | 'collaboration'
  | 'events'
  | 'automation'

const PLAN_RANK: Record<string, number> = {
  trial: 3,
  starter: 1,
  growth: 2,
  professional: 3,
}

export const FEATURE_MIN_PLAN: Record<PlanFeature, 'starter' | 'growth' | 'professional'> = {
  orders: 'starter',
  payments: 'starter',
  products: 'starter',
  inventory: 'starter',
  customers: 'starter',
  website: 'starter',
  journal: 'starter',
  staff: 'starter',
  reports: 'starter',
  quotations: 'growth',
  purchase_orders: 'growth',
  rentals: 'growth',
  scheduling: 'growth',
  payroll: 'growth',
  marketing: 'growth',
  messaging: 'growth',
  collaboration: 'growth',
  events: 'growth',
  automation: 'starter',
}

export const ALL_PLAN_FEATURES = Object.keys(FEATURE_MIN_PLAN) as PlanFeature[]

export function planHasFeature(planCode: string | null | undefined, feature: PlanFeature): boolean {
  const plan = String(planCode || 'trial').toLowerCase()
  const planRank = PLAN_RANK[plan]
  if (plan === 'trial' || planRank == null) return true
  const minPlan = FEATURE_MIN_PLAN[feature]
  const minRank = minPlan != null ? PLAN_RANK[minPlan] : undefined
  if (minRank == null) return true
  return planRank >= minRank
}

export function featuresForPlan(planCode: string | null | undefined): PlanFeature[] {
  return ALL_PLAN_FEATURES.filter((feature) => planHasFeature(planCode, feature))
}

/** Dashboard href → plan feature. Unlisted staff routes stay available on every plan. */
export const FEATURE_BY_HREF: Record<string, PlanFeature> = {
  '/dashboard/quotations': 'quotations',
  '/dashboard/suppliers': 'purchase_orders',
  '/dashboard/purchase-orders': 'purchase_orders',
  '/dashboard/vendor-bills': 'purchase_orders',
  '/dashboard/subscription': 'rentals',
  '/dashboard/leases': 'rentals',
  '/dashboard/schedule': 'scheduling',
  '/dashboard/schedule/resources': 'scheduling',
  '/dashboard/class-packages': 'scheduling',
  '/dashboard/bookings': 'scheduling',
  '/dashboard/payroll': 'payroll',
  '/dashboard/staff-time-entries': 'payroll',
  '/dashboard/staff-leave': 'payroll',
  '/dashboard/coupons': 'marketing',
  '/dashboard/membership': 'marketing',
  '/dashboard/referrers': 'marketing',
  '/dashboard/companion-bundles': 'marketing',
  '/dashboard/social-promote': 'marketing',
  '/dashboard/messaging/templates': 'messaging',
  '/dashboard/messaging/send': 'messaging',
  '/dashboard/teams': 'collaboration',
  '/dashboard/collaboration': 'collaboration',
  '/dashboard/team-files': 'collaboration',
  '/dashboard/data-management': 'collaboration',
  '/dashboard/events': 'events',
  '/dashboard/events/forms': 'events',
  '/dashboard/automation-tasks': 'automation',
}

export function planFeatureForPath(pathname: string): PlanFeature | null {
  const path = pathname.split('?')[0] || ''
  const exact = FEATURE_BY_HREF[path]
  if (exact) return exact
  const hit = Object.keys(FEATURE_BY_HREF)
    .sort((a, b) => b.length - a.length)
    .find((href) => path === href || path.startsWith(`${href}/`))
  return hit ? FEATURE_BY_HREF[hit] ?? null : null
}
